// The handover flow of plan §6, shared by the door kiosk and the teacher app's roaming handover
// (§6 step 8). Everything device-specific (camera, face detector, QR decoder, photo source, the
// outbox write) is injected, so a phone passes its own camera and the same outbox.
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Banner, Button, Dialog, Icon, PhotoTile, PinPad, Select, TextField } from "@vavr/ui";
import { checkQr, matchDelegationCode, StaffLock, type CaptureBlob, type Roster, type SiteInfo } from "@vavr/client";
import { canonicalize, checkPin, ID_TYPES, sha256Hex, type PreImage } from "@vavr/core";
import type { Camera } from "../camera.ts";
import type { NfcReaderBridge } from "../../shared/platform.ts";
import type { FaceDetectorLike } from "../face.ts";
import { usePhoto, type PhotoSource } from "../photos.ts";
import { passProblem, type QrDecoder } from "../qr-scan.ts";
import { buildEvents, missing, reduce, start, tilesFor, type Action, type Child, type Direction, type FlowState, type IdType } from "./machine.ts";
import { FindChild } from "./FindChild.tsx";
import { Finder } from "../Finder.tsx";
import { QrScanner } from "./QrScanner.tsx";
import { SignStep, type Capture } from "./SignStep.tsx";
import { LiveVideo, RestrictionBanners, StaffStrip } from "./StaffStrip.tsx";
// The flow carries its own styles: the teacher app mounts it without KioskApp.
import "../kiosk.css";

export interface HandoverDone {
  direction: Direction;
  firstNames: string[];
  at: number;
  events: PreImage[];
}

export interface HandoverFlowProps {
  /**
   * "kiosk" = the staffed wall tablet; "roaming" = a teacher's phone (same screens, phone camera);
   * "self_service" = the unstaffed wall tablet, where a parent sends a pickup request.
   */
  mode: "kiosk" | "roaming" | "self_service";
  direction: Direction;
  roster: Roster;
  site: SiteInfo;
  /** The staff member on duty; null on the self-service kiosk. */
  staff: { id: string; name: string } | null;
  deviceId: string;
  /** performance.timeOrigin-style start for device_mono. */
  monoStart: number;
  camera: Camera | null;
  photos: PhotoSource;
  loadDetector?: () => Promise<FaceDetectorLike | null>;
  makeQrDecoder?: () => Promise<QrDecoder>;
  /** Writes the signed events and their blobs to the outbox. Must not wait for the network. */
  seal: (events: PreImage[], blobs: CaptureBlob[]) => Promise<void>;
  onDone: (r: HandoverDone) => void;
  onCancel: () => void;
  /** Children handled here today, most recent first. */
  today?: string[];
  /** Abandoned flows return to idle after this long without a touch (kiosk only). */
  idleMs?: number;
  /** Open the pass scanner at once (the kiosk's "Scan your pass" button). */
  startScanning?: boolean;
  /** Actions applied before the first render: the home screen's chosen children, or a pass it read. */
  startWith?: Action[];
  /** Android NFC reader: a parent's phone tapped while the children are being chosen reads as a pass. */
  nfc?: NfcReaderBridge;
  /** For tests and previews: start from a given state. */
  initial?: FlowState;
}

export const DEFAULT_ATTESTATION = {
  pickup: "I confirm I am collecting this child and that I am authorised to do so.",
  dropoff: "I am dropping off this child.",
};

const ID_LABEL: Record<IdType, string> = {
  drivers_license: "Driver's license", state_id: "State ID", passport: "Passport", military_id: "Military ID", other: "Other photo ID",
};

const TITLE: Record<FlowState["step"], (s: FlowState) => string> = {
  find: (s) => (s.direction === "pickup" ? "Who are you collecting?" : "Who are you dropping off?"),
  siblings: () => "Anyone else today?",
  who: () => "Who are you?",
  adult_pin: (s) => `${s.adult?.first ?? ""}, please enter your PIN`,
  code: () => "Enter your code",
  someone: () => "Tell us who you are",
  witness: () => "Staff: second PIN, please",
  typed_name: () => "Type your full name",
  sign: (s) => (s.selfService ? `Sign to ${s.direction === "dropoff" ? "drop off" : "ask for"} ${firstNames(s.children).join(" and ")}` : s.policy === "off" ? "Please look at the camera" : "Please sign below"),
  sealing: () => "Saving",
};

const firstNames = (cs: Child[]) => cs.map((c) => c.first);

/** The self-service step rail: which flow steps belong to Children, You and Sign. */
const RAIL: { label: string; steps: FlowState["step"][] }[] = [
  { label: "Children", steps: ["find", "siblings"] },
  { label: "You", steps: ["who", "adult_pin", "code", "someone", "witness"] },
  { label: "Sign", steps: ["sign", "typed_name", "sealing"] },
];

/** The line under each self-service question. */
const ASK_LEAD: Partial<Record<FlowState["step"], string>> = {
  find: "Tap every child, then Next. Type the first letters to find a name.",
  siblings: "Your pass covers these children. Tap to leave anyone out.",
  who: "Tap your photo. If you have a PIN, you enter it next.",
  adult_pin: "The PIN you chose with the school. Staff cannot see it.",
  code: "The 10-character code the family sent you.",
  someone: "The member of staff who brings your child checks your ID.",
  typed_name: "Instead of a drawn signature.",
  sign: "The camera takes your photo while you sign.",
};

function KidFace({ c, photos }: { c: Child; photos: PhotoSource }) {
  const src = usePhoto(photos, c.photo_sha256);
  return src ? <img className="pmk-ask__face" src={src} alt="" /> : <span className="pmk-ask__face pmk-ask__face--none" aria-hidden="true">{c.first.slice(0, 1)}</span>;
}

function AdultTile({ a, photos, onSelect }: { a: ReturnType<typeof tilesFor>[number]; photos: PhotoSource; onSelect: () => void }) {
  const src = usePhoto(photos, a.photo_sha256);
  return <PhotoTile name={`${a.first} ${a.last}`} detail={a.relation} src={src} onSelect={onSelect} />;
}

function SiblingTile({ c, photos, selected, onSelect }: { c: Child; photos: PhotoSource; selected: boolean; onSelect: () => void }) {
  const src = usePhoto(photos, c.photo_sha256);
  return <PhotoTile kind="child" name={`${c.first} ${c.last}`} src={src} selected={selected} onSelect={onSelect} />;
}

export function HandoverFlow(p: HandoverFlowProps) {
  const [s, dispatch] = useReducer(reduce, undefined, () => p.initial ?? (p.startWith ?? []).reduce(reduce, start({
    direction: p.direction, roster: p.roster, staffId: p.staff?.id ?? null, selfService: p.mode === "self_service",
    settings: p.site.settings, state: p.site.state,
  })));
  // Self-service: the children picked on the Children step, kept if the parent comes back to it.
  const [picked, setPicked] = useState<string[]>(() => s.children.map((c) => c.id));
  const [scanning, setScanning] = useState(Boolean(p.startScanning && p.makeQrDecoder));
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [someone, setSomeone] = useState<{ name: string; idType: IdType; last4: string }>({ name: "", idType: "drivers_license", last4: "" });
  const [typed, setTyped] = useState("");
  const [witnessTries, setWitnessTries] = useState(0);
  const [faceHint, setFaceHint] = useState("Now");
  const capture = useRef<Capture>({ photo: null, face: null, recorder: null });
  const witnessLock = useRef(new StaffLock());
  const attestation = (p.direction === "pickup" ? p.site.settings.attestation_pickup : p.site.settings.attestation_dropoff)?.trim()
    || DEFAULT_ATTESTATION[p.direction];

  // An abandoned flow on the wall tablet returns to idle; nothing is recorded.
  useEffect(() => {
    if (p.mode === "roaming" || s.step === "sealing") return;
    let t = setTimeout(p.onCancel, p.idleMs ?? 90_000);
    const bump = () => { clearTimeout(t); t = setTimeout(p.onCancel, p.idleMs ?? 90_000); };
    window.addEventListener("pointerdown", bump);
    window.addEventListener("keydown", bump);
    return () => { clearTimeout(t); window.removeEventListener("pointerdown", bump); window.removeEventListener("keydown", bump); };
  }, [p.mode, p.idleMs, p.onCancel, s.step]);

  // Sealing: hash the blobs, build one signed pre-image per child, write them to the outbox.
  useEffect(() => {
    if (s.step !== "sealing") return;
    let live = true;
    (async () => {
      setBusy(true);
      try {
        const c = capture.current;
        if (!c.photo && p.camera) { c.photo = await p.camera.capture(); c.face = null; }
        const blobs: CaptureBlob[] = [];
        let photoSha: string | null = null;
        let strokeSha: string | null = null;
        if (c.photo) { photoSha = await sha256Hex(c.photo.bytes); blobs.push({ bytes: c.photo.bytes, type: "image/jpeg" }); }
        if (s.signature === "drawn" && s.policy !== "off" && c.recorder) {
          const bytes = new TextEncoder().encode(canonicalize(c.recorder.toStroke()));
          strokeSha = await sha256Hex(bytes);
          blobs.push({ bytes, type: "application/json" });
        }
        const events = buildEvents(s, {
          siteId: p.site.id, deviceId: p.deviceId, monoStart: p.monoStart, attestation, photoSha256: photoSha,
          face: photoSha ? c.face : null, strokeSha256: strokeSha, offline: typeof navigator !== "undefined" && navigator.onLine === false,
        });
        await p.seal(events, blobs);
        if (live) p.onDone({ direction: s.direction, firstNames: firstNames(s.children), at: events[0]!.device_ts, events });
      } catch (e) {
        console.error("seal failed", e);
        if (live) dispatch({ type: "seal_failed", message: "This could not be saved. Please try again, or ask a member of staff." });
      } finally {
        if (live) setBusy(false);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.step]);

  const tiles = useMemo(() => (s.step === "who" ? tilesFor(s) : []), [s]);
  const onQrText = async (text: string) => {
    const site = p.site;
    const r = await checkQr(p.roster, text, site.sign_pubkey);
    if (!r.ok) { dispatch({ type: "error", message: passProblem(r.reason) }); return; }
    setScanning(false);
    dispatch({ type: "qr_ok", adultId: r.pass.adult_id, childIds: r.childIds });
  };
  // NFC: while the children are being chosen, a parent's phone held to this device is their pass.
  const onQr = useRef(onQrText);
  onQr.current = onQrText;
  const nfcStep = s.step === "find";
  const [nfcReady, setNfcReady] = useState(false);
  useEffect(() => {
    if (!p.nfc || !nfcStep) return;
    let stop: (() => void) | null = null;
    let live = true;
    p.nfc.listen((t) => void onQr.current(t), (m) => dispatch({ type: "error", message: m }))
      .then((f) => { if (live) { stop = f; setNfcReady(true); } else f(); }, () => undefined);
    return () => { live = false; setNfcReady(false); stop?.(); };
  }, [p.nfc, nfcStep]);

  const checkWitness = async (pin: string) => {
    const r = await witnessLock.current.unlock(p.roster, pin);
    setWitnessTries((n) => n + 1);
    if (r.ok) dispatch({ type: "witness_result", staffId: r.staff.id });
    else if (r.reason === "locked") dispatch({ type: "error", message: "Too many tries. Please wait a few minutes." });
    else dispatch({ type: "witness_result", staffId: null });
  };

  // Restrictions are for staff: the self-service kiosk never shows them to the parent, and the
  // staff member who releases the child sees and acknowledges them instead.
  const needsAck = !s.selfService && s.step === "sign" && s.restrictions.length > 0 && !s.restrictionAck;
  const showStrip = !s.selfService && (s.step === "who" || s.step === "sign" || s.step === "adult_pin" || s.step === "code" || s.step === "someone" || s.step === "witness");

  const body = (() => {
    switch (s.step) {
      case "find":
        if (s.selfService) {
          return (
            <div className="pmk-children">
              <Finder roster={p.roster} photos={p.photos} selected={picked} bare
                onToggle={(id) => setPicked((x) => (x.includes(id) ? x.filter((y) => y !== id) : [...x, id]))} />
              <div className="pmk-children__next">
                <Button size="lg" iconEnd="chevron-right" disabled={!picked.length} onClick={() => dispatch({ type: "choose_children", childIds: picked })}>
                  {picked.length ? `Next: ${picked.length === 1 ? "1 child" : `${picked.length} children`}` : "Tap a child first"}
                </Button>
              </div>
            </div>
          );
        }
        return (
          <FindChild
            roster={p.roster} direction={s.direction} photos={p.photos} today={p.today ?? []}
            onSelect={(childId) => dispatch({ type: "select_child", childId })}
            onScan={p.makeQrDecoder ? () => { dispatch({ type: "clear_error" }); setScanning(true); } : undefined}
          />
        );
      case "siblings":
        return (
          <div className="pmk-stack">
            <p className="pmk-lead">Tap every child going {s.direction === "pickup" ? "home" : "in"} with you now.</p>
            <div className="pmk-tiles">
              {s.offer.map((c) => (
                <SiblingTile key={c.id} c={c} photos={p.photos} selected={s.children.some((x) => x.id === c.id)} onSelect={() => dispatch({ type: "toggle_child", childId: c.id })} />
              ))}
            </div>
            <div className="pmk-actions">
              <Button size="lg" iconEnd="chevron-right" disabled={!s.children.length} onClick={() => dispatch({ type: "confirm_children" })}>
                {s.children.length > 1 ? `Continue with ${s.children.length} children` : "Continue"}
              </Button>
            </div>
          </div>
        );
      case "who":
        return (
          <div className="pmk-stack">
            {tiles.length ? (
              <div className="pmk-tiles">
                {tiles.map((a) => <AdultTile key={a.id} a={a} photos={p.photos} onSelect={() => dispatch({ type: "choose_adult", adultId: a.id })} />)}
              </div>
            ) : (
              <p className="pmk-lead">{`Nobody is on the list for ${firstNames(s.children).join(" and ")} right now. Choose Someone else, or ask a member of staff.`}</p>
            )}
            <div className="pmk-actions pmk-actions--start">
              <Button variant="secondary" icon="user" onClick={() => dispatch({ type: "choose_someone" })}>Someone else</Button>
              <Button variant="secondary" icon="qr" onClick={() => dispatch({ type: "choose_code" })}>I have a code</Button>
            </div>
          </div>
        );
      case "adult_pin":
        return (
          <div className="pmk-pinstep">
            <p className="pmk-lead">The PIN you chose with the school or center. Staff cannot see it.</p>
            <PinPad label="Your 4-digit PIN" autoFocus error={s.error ?? undefined} errorKey={s.pinFailures}
              onChange={() => s.error && dispatch({ type: "clear_error" })}
              onComplete={async (pin) => dispatch({ type: "pin_result", ok: await checkPin(s.adult!.id, pin, s.adult!.pin_hash) })} />
          </div>
        );
      case "code":
        return (
          <form className="pmk-form" onSubmit={async (e) => {
            e.preventDefault();
            const d = s.children[0] ? await matchDelegationCode(p.roster, s.children[0].id, code) : null;
            dispatch({ type: "code_result", delegation: d });
          }}>
            <TextField label="Your code" hint="The 10-character code the family sent you" value={code} autoComplete="off" autoCapitalize="characters" spellCheck={false}
              onChange={(e) => setCode(e.currentTarget.value)} autoFocus />
            <Button type="submit" size="lg" disabled={code.replace(/[\s-]/g, "").length < 10}>Check code</Button>
          </form>
        );
      case "someone":
        return (
          <form className="pmk-form" onSubmit={(e) => { e.preventDefault(); dispatch({ type: "someone_details", ...someone }); }}>
            <TextField label="Your full name" value={someone.name} autoComplete="off" required autoFocus
              onChange={(e) => { const name = e.currentTarget.value; setSomeone((x) => ({ ...x, name })); }} />
            <div className="pmk-form__row">
              <Select label="Photo ID shown to staff" value={someone.idType} options={ID_TYPES.map((t) => ({ value: t, label: ID_LABEL[t] }))}
                onValueChange={(v) => v && setSomeone((x) => ({ ...x, idType: v as IdType }))} />
              <TextField label="Last 4 of ID number" value={someone.last4} maxLength={4} autoComplete="off" autoCapitalize="characters" spellCheck={false} required
                onChange={(e) => { const last4 = e.currentTarget.value.toUpperCase(); setSomeone((x) => ({ ...x, last4 })); }} />
            </div>
            <p className="pmk-note">{s.selfService ? "Have your ID ready: the member of staff who brings your child checks it." : "A member of staff checks your ID and enters their PIN next."}</p>
            <Button type="submit" size="lg">Continue</Button>
          </form>
        );
      case "witness":
        return (
          <div className="pmk-pinstep">
            <p className="pmk-lead">
              {s.witnessFor === "unlisted_adult" ? `A second member of staff (not ${p.staff?.name ?? "the one on duty"}) checks the ID and enters their PIN.`
                : "A second member of staff witnesses this handover and enters their PIN."}
            </p>
            <PinPad label="Witness PIN" autoFocus error={s.error ?? undefined} errorKey={witnessTries}
              onChange={() => s.error && dispatch({ type: "clear_error" })} onComplete={checkWitness} />
          </div>
        );
      case "typed_name":
        return (
          <form className="pmk-form" onSubmit={(e) => { e.preventDefault(); dispatch({ type: "typed_name", name: typed }); }}>
            <TextField label="Your full name" hint="Instead of a drawn signature. A member of staff witnesses it." value={typed} autoComplete="off" autoFocus
              onChange={(e) => setTyped(e.currentTarget.value)} required />
            <Button type="submit" size="lg">Continue</Button>
          </form>
        );
      case "sign":
      case "sealing":
        return (
          <SignStep
            mirror={s.selfService ? (
              <figure className="pmk-mirror" data-ok={faceHint === "Photo taken" || undefined}>
                <LiveVideo stream={p.camera?.stream ?? null} label="You, on the camera" className="pmk-video--mirror" />
                <figcaption aria-live="polite">{faceHint === "Now" ? "Look here" : faceHint}</figcaption>
              </figure>
            ) : undefined}
            state={s} attestation={attestation} camera={p.camera} loadDetector={p.loadDetector} capture={capture.current} busy={busy || s.step === "sealing"}
            onStroke={(enough) => dispatch({ type: "stroke", enough })}
            onCannotSign={() => dispatch({ type: "cannot_sign" })}
            onTypedName={() => dispatch({ type: "typed_name_start" })}
            onSkip={() => dispatch({ type: "skip_signature" })}
            onSubmit={() => dispatch({ type: "submit" })}
            onFaceHint={setFaceHint}
          />
        );
    }
  })();

  const childLine = s.children.length ? `${s.selfService ? (s.direction === "dropoff" ? "Dropping off" : "Asking for") : s.direction === "pickup" ? "Collecting" : "Dropping off"} ${firstNames(s.children).join(" and ")}` : null;
  const pinOwnsError = s.step === "adult_pin" || s.step === "witness";

  return (
    <div className={`pmk-flow pmk-flow--${p.mode}`} data-step={s.step}>
      {s.selfService ? (
        <header className="pmk-rail">
          <button type="button" className="pmk-rail__back" onClick={() => (s.step === "find" ? p.onCancel() : dispatch({ type: "back" }))} disabled={s.step === "sealing"} aria-label="Back"><Icon name="chevron-left" /></button>
          <ol className="pmk-rail__steps" aria-label="Steps">
            {RAIL.map((r, i) => {
              const at = RAIL.findIndex((x) => x.steps.includes(s.step));
              const state = i < at ? "done" : i === at ? "now" : "next";
              return <li key={r.label} className={`pmk-rail__step pmk-rail__step--${state}`} aria-current={state === "now" ? "step" : undefined}><i>{state === "done" ? <Icon name="check" /> : i + 1}</i>{r.label}</li>;
            })}
          </ol>
          <button type="button" className="pmk-rail__cancel" onClick={p.onCancel} disabled={s.step === "sealing"}>Cancel</button>
        </header>
      ) : (
      <header className="pmk-bar">
        <Button variant="ghost" icon="chevron-left" onClick={() => (s.step === "find" ? p.onCancel() : dispatch({ type: "back" }))} disabled={s.step === "sealing"}>Back</Button>
        <div className="pmk-bar__title">
          <h1 className="pmk-title">{TITLE[s.step](s)}</h1>
          {childLine && s.step !== "find" && <p className="pmk-bar__sub">{childLine}</p>}
        </div>
        <Button variant="ghost" icon="close" onClick={p.onCancel} disabled={s.step === "sealing"}>Cancel</Button>
      </header>
      )}
      {s.error && !pinOwnsError && <Banner tone="warning" title={s.error} className="pmk-error" />}
      {nfcReady && nfcStep && <p className="pmk-nfc" role="status">Ready to tap: hold the parent's phone to the back of this one to read their pass.</p>}
      <div className={`pmk-body ${showStrip ? "pmk-body--strip" : ""} ${s.selfService ? "pmk-body--ask" : ""}`}>
        {s.selfService && (
          <div className="pmk-ask">
            <h1 className="pmk-ask__title">{TITLE[s.step](s)}</h1>
            {ASK_LEAD[s.step] && <p className="pmk-ask__lead">{ASK_LEAD[s.step]}</p>}
            {s.children.length > 0 && (
              <div className="pmk-ask__kids">
                <span className="pmk-ask__faces">{s.children.map((c) => <KidFace key={c.id} c={c} photos={p.photos} />)}</span>
                <span>{childLine}</span>
              </div>
            )}
          </div>
        )}
        <div className="pmk-main">{body}</div>
        {showStrip && (
          <StaffStrip
            adult={s.adult} someoneName={s.someone?.name ?? null} staffName={p.staff?.name ?? null} stream={p.camera?.stream ?? null}
            photos={p.photos} restrictions={s.selfService ? [] : s.restrictions} nameMatches={s.selfService ? [] : s.nameMatches} roster={p.roster}
            caption={s.step === "sign" || s.step === "sealing" ? faceHint : "Now"}
          />
        )}
      </div>
      {scanning && (
        <QrScanner stream={p.camera?.stream ?? null} video={p.camera?.video ?? null} makeDecoder={p.makeQrDecoder!}
          onText={onQrText} onClose={() => { setScanning(false); dispatch({ type: "clear_error" }); }} error={s.step === "find" ? s.error : null} />
      )}
      <Dialog
        open={needsAck} dismissible={false} title="Staff: check before continuing"
        description="Read the restriction below and check the adult's photo against the camera."
        actions={(
          <>
            <Button variant="secondary" onClick={p.onCancel}>Stop this handover</Button>
            <Button onClick={() => dispatch({ type: "ack_restriction" })}>I have checked</Button>
          </>
        )}
      >
        <RestrictionBanners restrictions={s.restrictions} nameMatches={s.nameMatches} roster={p.roster} />
      </Dialog>
      <p className="pmu-sr-only" aria-live="polite">{s.step === "sign" ? missing(s).join(" ") : ""}</p>
    </div>
  );
}
